// Payments API (spec §2, §7–§11): context, limit checks, FX quote,
// authorisation (MPIN + OTP), idempotent execution and history.
import type {
  AuthSession,
  ExecutePaymentInput,
  FxQuote,
  LimitCheckInput,
  LimitCheckResult,
  Payment,
  PaymentApproval,
  PaymentContext,
  RecentUpiPayee,
} from '@/types/phase2.types';
import { ApiError, createIdempotencyKey, deviceInfo, fromPaise, get, getBlob, post, toPaise } from './client';
import { MOCK_PAYMENTS, REAL_API_ENABLED } from './flags';
import * as mock from '@/mock/handlers/payments';

const notAvailable = (): never => {
  throw new ApiError('NOT_AVAILABLE', 'This feature is not available on the real API yet.');
};

export interface TransactionRecord {
  id: string;
  transactionId: string;
  referenceNumber?: string;
  transactionType?: string;
  channel?: string;
  status?: string;
  amount: number;
  currency?: string;
  sourceAccountNumber?: string;
  destinationAccountNumber?: string;
  direction?: string;
  category?: string;
  description?: string;
  narration?: string;
  valueDate?: string;
  runningBalance?: number;
  initiatedAt?: string;
  completedAt?: string;
  failureReason?: string;
  [key: string]: unknown;
}

export interface RtgsNeftImpsTransferInput {
  amountPaise: number;
  currency: string;
  channel: string;
  description?: string;
  idempotencyKey?: string;
  fromAccountNumber: string;
  beneficiaryAccountNumber: string;
  beneficiaryIfsc: string;
  beneficiaryName: string;
}

export interface PaymentRequest {
  debitAccountNumber: string;
  rail: string;
  amountPaise: number;
  beneficiaryAccountNumber?: string;
  beneficiaryIfsc?: string;
  beneficiaryName?: string;
  payeeVpa?: string;
  remarks?: string;
  mpin?: string;
  upiPin?: string;
}

export interface InternalTransferInput {
  amountPaise: number;
  currency: string;
  channel: string;
  description?: string;
  idempotencyKey?: string;
  fromAccountNumber: string;
  toAccountNumber: string;
}

export interface DebitTransactionInput {
  amountPaise: number;
  currency: string;
  channel: string;
  description?: string;
  idempotencyKey?: string;
  accountNumber: string;
  beneficiaryAccountNumber?: string;
  beneficiaryIfsc?: string;
}

export interface CreditTransactionInput {
  amountPaise: number;
  currency: string;
  channel: string;
  description?: string;
  idempotencyKey?: string;
  accountNumber: string;
  remitterAccountNumber?: string;
}

const transactionBody = <T extends { amountPaise: number; idempotencyKey?: string }>(input: T) => {
  const { amountPaise, idempotencyKey, ...rest } = input;
  return { ...rest, amount: amountPaise, idempotencyKey: idempotencyKey ?? createIdempotencyKey() };
};

const transactionPost = (path: string, body: unknown): Promise<TransactionRecord> =>
  REAL_API_ENABLED
    ? post<TransactionRecord>(path, body)
    : notAvailable();

export const initiateRtgs = (input: RtgsNeftImpsTransferInput): Promise<TransactionRecord> =>
  transactionPost('/transactions/rtgs', transactionBody(input));

export const initiateNeft = (input: RtgsNeftImpsTransferInput): Promise<TransactionRecord> =>
  transactionPost('/transactions/neft', transactionBody(input));

export const initiateImps = (input: RtgsNeftImpsTransferInput): Promise<TransactionRecord> =>
  transactionPost('/transactions/imps', transactionBody(input));

export const initiateInternalTransfer = (input: InternalTransferInput): Promise<TransactionRecord> =>
  transactionPost('/transactions/internal-transfer', transactionBody(input));

export const initiateDebitTransaction = (input: DebitTransactionInput): Promise<TransactionRecord> =>
  transactionPost('/transactions/debit', transactionBody(input));

export const initiateCreditTransaction = (input: CreditTransactionInput): Promise<TransactionRecord> =>
  transactionPost('/transactions/credit', transactionBody(input));

export const reverseTransaction = (input: {
  originalTransactionId: string;
  reason: string;
  idempotencyKey?: string;
}): Promise<TransactionRecord> =>
  transactionPost('/transactions/reversal', {
    ...input,
    idempotencyKey: input.idempotencyKey ?? createIdempotencyKey(),
  });

export const getTransaction = (transactionId: string): Promise<TransactionRecord> =>
  REAL_API_ENABLED
    ? get<TransactionRecord>(`/transactions/${encodeURIComponent(transactionId)}`)
    : notAvailable();

export const downloadTransactionReceipt = (transactionId: string): Promise<Blob> =>
  REAL_API_ENABLED
    ? getBlob(`/transactions/${encodeURIComponent(transactionId)}/receipt`)
    : notAvailable();

const realMpinAuth = async (mpin: string): Promise<{ authTicket: string }> => {
  const username = localStorage.getItem('customerId') ?? '';
  try {
    const result = await post<{ authTicket?: string; token?: string }>('/auth/mpin/verify', {
      username,
      mpin,
      deviceInfo: { ...deviceInfo(), channel: 'WEB' },
    });
    const authTicket = result.authTicket ?? result.token;
    if (!authTicket) throw new ApiError('MPIN_RESPONSE_INVALID', 'MPIN verification response was invalid.');
    return { authTicket };
  } catch (error) {
    if (error instanceof ApiError) {
      if (error.code === 'PIN_001') throw new ApiError('PIN_001', 'Your MPIN is not set. Open MPIN setup to continue.');
      if (error.code === 'PIN_002' || error.code === 'AUTH_001') throw new ApiError('PIN_002', 'That MPIN is incorrect. Try again.');
      if (error.code === 'PIN_003') throw new ApiError('PIN_003', 'Your MPIN is locked. Use Forgot MPIN.');
    }
    throw error;
  }
};

const paymentStatus = (value: unknown): Payment['status'] => {
  const status = String(value ?? 'PENDING').toUpperCase();
  if (status === 'COMPLETED' || status === 'EXECUTED') return 'SUCCESS';
  if (status === 'SUCCESS' || status === 'PENDING' || status === 'FAILED' || status === 'PENDING_APPROVAL' || status === 'APPROVED' || status === 'REJECTED' || status === 'CANCELLED') return status;
  return 'PENDING';
};

const timestamp = (value: unknown): number => {
  const parsed = new Date(String(value ?? '')).getTime();
  return Number.isFinite(parsed) ? parsed : Date.now();
};

const optionalTimestamp = (value: unknown): number | undefined => {
  if (value === undefined || value === null || value === '') return undefined;
  const parsed = new Date(String(value)).getTime();
  return Number.isFinite(parsed) ? parsed : undefined;
};

const mapPayment = (raw: Record<string, unknown>): Payment => ({
  id: String(raw.id ?? raw.paymentId ?? raw.approvalId ?? raw.reference ?? ''),
  reference: String(raw.reference ?? raw.referenceNumber ?? raw.id ?? ''),
  idempotencyKey: String(raw.idempotencyKey ?? ''),
  rail: (['UPI', 'NOVABANK', 'NEFT', 'IMPS', 'RTGS', 'INTERNATIONAL'].includes(String(raw.rail ?? '').toUpperCase()) ? String(raw.rail).toUpperCase() : 'IMPS') as Payment['rail'],
  amountRupees: fromPaise(Number(raw.amountPaise ?? raw.amount ?? 0)),
  chargesRupees: fromPaise(Number(raw.chargesPaise ?? raw.charges ?? 0)),
  status: paymentStatus(raw.status),
  approvalId: typeof raw.approvalId === 'string' ? raw.approvalId : typeof (raw.approval as Record<string, unknown> | undefined)?.transactionId === 'string' ? String((raw.approval as Record<string, unknown>).transactionId) : undefined,
  levelsRequired: typeof raw.levelsRequired === 'number' ? raw.levelsRequired : typeof (raw.approval as Record<string, unknown> | undefined)?.levelsRequired === 'number' ? Number((raw.approval as Record<string, unknown>).levelsRequired) : undefined,
  currentLevel: typeof raw.currentLevel === 'number' ? raw.currentLevel : typeof (raw.approval as Record<string, unknown> | undefined)?.currentLevel === 'number' ? Number((raw.approval as Record<string, unknown>).currentLevel) : undefined,
  approvalMessage: typeof raw.message === 'string' ? raw.message : undefined,
  rejectionReason: typeof raw.rejectionReason === 'string' ? raw.rejectionReason : undefined,
  cancelledAt: optionalTimestamp(raw.cancelledAt),
  failureReason: typeof raw.failureReason === 'string' ? raw.failureReason : undefined,
  payeeName: String(raw.payeeName ?? raw.beneficiaryName ?? ''),
  beneficiaryId: typeof raw.beneficiaryId === 'string' ? raw.beneficiaryId : undefined,
  accountId: typeof raw.accountId === 'string' ? raw.accountId : undefined,
  destination: String(raw.destination ?? raw.payeeVpa ?? raw.beneficiaryAccountNumber ?? ''),
  purpose: typeof raw.purpose === 'string' ? raw.purpose : undefined,
  remark: typeof raw.remark === 'string' ? raw.remark : typeof raw.remarks === 'string' ? raw.remarks : undefined,
  createdAt: timestamp(raw.createdAt ?? raw.initiatedAt),
});

const realPaymentContext = async (accountNumber?: string): Promise<PaymentContext> => {
  const data = await get<Record<string, unknown>>('/payments/context', accountNumber ? { params: { accountNumber } } : undefined);
  return {
    // /payments/context returns availableBalance in rupees.
    balanceRupees: Number(data.availableBalance ?? 0),
    productType: String(data.productType ?? 'SAVINGS').toUpperCase() as PaymentContext['productType'],
    dailyLimitRupees: fromPaise(Number(data.dailyLimitPaise ?? data.dailyLimit ?? 0)),
    dailyUsedRupees: fromPaise(Number(data.dailyUsedPaise ?? data.dailyUsed ?? 0)),
    upiDailyCapRupees: fromPaise(Number(data.upiDailyCapPaise ?? data.upiDailyCap ?? 0)),
    upiUsedRupees: fromPaise(Number(data.upiUsedPaise ?? data.upiUsed ?? 0)),
    upiEnabled: Boolean(data.upiEnabled ?? true),
    newDeviceUntil: typeof data.newDeviceUntil === 'number' ? data.newDeviceUntil : undefined,
    accountNumber: typeof data.accountNumber === 'string' ? data.accountNumber : undefined,
    mpinSet: typeof data.mpinSet === 'boolean' ? data.mpinSet : undefined,
  };
};

export const getPaymentContext = (seed?: {
  balanceRupees?: number;
  productType?: PaymentContext['productType'];
  accountNumber?: string;
}): Promise<PaymentContext> => MOCK_PAYMENTS ? mock.getPaymentContext(seed) : realPaymentContext(seed?.accountNumber);

const paymentRequestBody = (input: PaymentRequest): PaymentRequest => ({
  ...input,
  rail: input.rail === 'NOVABANK' ? 'NOVA_BANK' : input.rail,
});

const paymentValidationResult = (data: Record<string, unknown>): LimitCheckResult => {
  const approvalRequired = Boolean(data.approvalRequired);
  const approvalLevels = Number(data.approvalLevels ?? 0);
  return data.ok === false || data.valid === false
    ? { ok: false, message: String(data.failureMessage ?? data.message ?? 'This payment cannot be processed.'), approvalRequired, approvalLevels }
    : { ok: true, approvalRequired, approvalLevels };
};

export const validateTransfer = async (input: PaymentRequest): Promise<Record<string, unknown>> =>
  REAL_API_ENABLED
    ? post<Record<string, unknown>>('/transfers/validate', paymentRequestBody(input))
    : notAvailable();

export const validatePayment = async (input: PaymentRequest): Promise<Record<string, unknown>> =>
  REAL_API_ENABLED
    ? post<Record<string, unknown>>('/payments/validate', paymentRequestBody(input))
    : notAvailable();

export const sendTransfer = async (
  input: PaymentRequest,
  idempotencyKey?: string
): Promise<Payment> => {
  const data = await post<Record<string, unknown>>('/transfers', paymentRequestBody(input), {
    headers: { 'Idempotency-Key': idempotencyKey || createIdempotencyKey() },
  });
  return mapPayment(data);
};

export const sendPayment = async (
  input: PaymentRequest,
  idempotencyKey?: string
): Promise<Payment> => {
  const data = await post<Record<string, unknown>>('/payments', paymentRequestBody(input), {
    headers: { 'Idempotency-Key': idempotencyKey || createIdempotencyKey() },
  });
  return mapPayment(data);
};

export const checkLimits = async (input: LimitCheckInput): Promise<LimitCheckResult> => {
  if (MOCK_PAYMENTS) return mock.checkLimitsApi(input);
  // The server rejects a blank debit account with ERR_006; say it plainly instead.
  if (!input.debitAccountNumber) {
    return { ok: false, message: 'We could not find your account. Refresh and try again.' };
  }
  const account = input.account && 'accountNumber' in input.account ? input.account.accountNumber : undefined;
  const request: PaymentRequest = {
    debitAccountNumber: input.debitAccountNumber,
    rail: input.rail,
    amountPaise: toPaise(input.amountRupees + (input.chargesRupees ?? 0)),
    beneficiaryAccountNumber: account,
    beneficiaryIfsc: input.beneficiaryIfsc,
    beneficiaryName: input.beneficiaryName,
    payeeVpa: input.payeeVpa,
    remarks: input.remarks,
    mpin: input.mpin,
    upiPin: input.upiPin,
  };
  // NovaBank transfers validate on /payments too; /transfers is only an alias.
  const data = await validatePayment(request);
  return paymentValidationResult(data);
};

export const getFxQuote = (currency: string): Promise<FxQuote> =>
  MOCK_PAYMENTS ? mock.getFxQuote(currency) : get<FxQuote>('/payments/fx-quote', { params: { currency } });

export const beginPaymentAuth = (approvalLine: string): Promise<AuthSession> =>
  MOCK_PAYMENTS
    ? mock.beginPaymentAuth(approvalLine)
    : REAL_API_ENABLED
      ? Promise.resolve({ authId: `mpin-${Date.now()}`, approvalLine, expiresAt: Date.now() + 180_000, expiresInSeconds: 180 })
      : notAvailable();

export const verifyPaymentMpin = (
  authId: string,
  mpin: string
): Promise<{ authTicket: string }> =>
  MOCK_PAYMENTS ? mock.verifyPaymentMpin(authId, mpin) : REAL_API_ENABLED ? realMpinAuth(mpin) : notAvailable();

export const verifyPaymentOtp = (authId: string, code: string): Promise<{ authTicket: string }> =>
  MOCK_PAYMENTS ? mock.verifyPaymentOtp(authId, code) : notAvailable();

export const resendPaymentOtp = (authId: string): Promise<{ expiresInSeconds: number }> =>
  MOCK_PAYMENTS ? mock.resendPaymentOtp(authId) : notAvailable();

export const executePayment = async (input: ExecutePaymentInput): Promise<Payment> => {
  if (MOCK_PAYMENTS) return mock.executePayment(input);
  if (!REAL_API_ENABLED) return notAvailable();
  const headers = { 'Idempotency-Key': input.idempotencyKey || createIdempotencyKey() };
  if (input.rail === 'UPI') {
    const data = await post<Record<string, unknown>>('/upi/pay', {
      debitAccountNumber: input.debitAccountNumber,
      payeeVpa: input.destination,
      payeeAccountNumber: input.accountId,
      amountPaise: toPaise(input.amountRupees),
      remarks: input.remark,
      upiPin: input.upiPin ?? input.authorizationPin,
    }, { headers });
    return mapPayment(data);
  }

  // Account-to-account rails: the unmasked account number and IFSC, with the
  // MPIN in the body. One call to /payments; no separate MPIN sign-in.
  const key = headers['Idempotency-Key'];
  const request: PaymentRequest = {
    debitAccountNumber: input.debitAccountNumber ?? '',
    rail: input.rail,
    amountPaise: toPaise(input.amountRupees),
    beneficiaryAccountNumber: input.beneficiaryAccountNumber,
    beneficiaryIfsc: input.beneficiaryIfsc,
    beneficiaryName: input.payeeName,
    payeeVpa: input.payeeVpa,
    remarks: input.remark,
    mpin: input.mpin ?? input.authorizationPin,
    upiPin: input.upiPin,
  };
  const payment = await sendPayment(request, key);
  // A direct payment comes back without the payee, destination or rail label,
  // so fill them from what the customer confirmed.
  return {
    ...payment,
    idempotencyKey: key,
    rail: input.rail,
    payeeName: payment.payeeName || input.payeeName,
    destination: input.destination,
    beneficiaryId: input.beneficiaryId,
    beneficiaryAlias: input.beneficiaryAlias,
    accountId: input.accountId,
    purpose: input.purpose,
    remark: input.remark,
  };
};

const approvalStatus = (value: unknown): PaymentApproval['status'] => {
  const status = String(value ?? '').toUpperCase();
  if (status === 'APPROVED' || status === 'COMPLETED' || status === 'SUCCESS') return 'EXECUTED';
  if (status === 'PENDING' || status === 'PENDING_APPROVAL') return 'PENDING_APPROVAL';
  if (status === 'REJECTED' || status === 'CANCELLED' || status === 'FAILED' || status === 'EXECUTED') return status;
  return 'FAILED';
};

// The server names the in-house rail NOVA_BANK; the app calls it NOVABANK.
const approvalRail = (value: unknown): PaymentApproval['rail'] => {
  const rail = String(value ?? '').toUpperCase().replace('NOVA_BANK', 'NOVABANK');
  return (['UPI', 'NOVABANK', 'NEFT', 'IMPS', 'RTGS', 'INTERNATIONAL'].includes(rail) ? rail : 'IMPS') as PaymentApproval['rail'];
};

const mapApproval = (raw: Record<string, unknown>): PaymentApproval => ({
  approvalId: String(raw.approvalId ?? raw.id ?? ''),
  reference: String(raw.reference ?? ''),
  status: approvalStatus(raw.status),
  amountPaise: Number(raw.amountPaise ?? 0),
  rail: approvalRail(raw.rail),
  debitAccountNumber: String(raw.debitAccountNumber ?? ''),
  beneficiaryName: String(raw.beneficiaryName ?? ''),
  beneficiaryAccountNumber: String(raw.beneficiaryAccountNumber ?? ''),
  beneficiaryIfsc: String(raw.beneficiaryIfsc ?? ''),
  remarks: typeof raw.remarks === 'string' ? raw.remarks : undefined,
  levelsRequired: Number(raw.levelsRequired ?? 0),
  currentLevel: Number(raw.currentLevel ?? 0),
  createdAt: timestamp(raw.createdAt),
  decidedAt: raw.decidedAt ? timestamp(raw.decidedAt) : undefined,
  rejectionReason: typeof raw.rejectionReason === 'string' ? raw.rejectionReason : undefined,
  failureReason: typeof raw.failureReason === 'string' ? raw.failureReason : undefined,
  transactionId: typeof raw.transactionId === 'string' ? raw.transactionId : undefined,
  utr: typeof raw.utr === 'string' ? raw.utr : undefined,
  executedAt: raw.executedAt ? timestamp(raw.executedAt) : undefined,
  cancelledAt: raw.cancelledAt ? timestamp(raw.cancelledAt) : undefined,
  canCancel: Boolean(raw.canCancel),
  message: String(raw.message ?? ''),
  steps: Array.isArray(raw.steps) ? raw.steps.map((step) => {
    const item = step as Record<string, unknown>;
    return { level: Number(item.level ?? 0), status: (['PENDING', 'APPROVED', 'REJECTED'].includes(String(item.status).toUpperCase()) ? String(item.status).toUpperCase() : 'PENDING') as 'PENDING' | 'APPROVED' | 'REJECTED', decidedAt: item.decidedAt ? timestamp(item.decidedAt) : undefined };
  }) : undefined,
});

const approvalRows = (data: unknown): Record<string, unknown>[] => {
  const rows = Array.isArray(data) ? data : (data as { content?: unknown[]; items?: unknown[] }).content ?? (data as { items?: unknown[] }).items ?? [];
  return rows.filter((row): row is Record<string, unknown> => Boolean(row && typeof row === 'object'));
};

export const listPaymentApprovals = async (status?: PaymentApproval['status']): Promise<PaymentApproval[]> => {
  if (MOCK_PAYMENTS) return mock.listPaymentApprovals(status);
  const data = await get<unknown>('/payments/approvals', status ? { params: { status } } : undefined);
  return approvalRows(data).map(mapApproval);
};

export const getPaymentApproval = async (approvalId: string): Promise<PaymentApproval> => {
  if (MOCK_PAYMENTS) return mock.getPaymentApproval(approvalId);
  return mapApproval(await get<Record<string, unknown>>(`/payments/approvals/${encodeURIComponent(approvalId)}`));
};

export const cancelPaymentApproval = async (approvalId: string): Promise<PaymentApproval> => {
  if (MOCK_PAYMENTS) return mock.cancelPaymentApproval(approvalId);
  return mapApproval(await post<Record<string, unknown>>(`/payments/approvals/${encodeURIComponent(approvalId)}/cancel`));
};

export const listPayments = async (): Promise<Payment[]> => {
  if (MOCK_PAYMENTS) return mock.listPayments();
  const data = await get<unknown>('/payments');
  const rows = Array.isArray(data) ? data : (data as { content?: unknown[]; items?: unknown[] }).content ?? (data as { items?: unknown[] }).items ?? [];
  return rows.filter((row): row is Record<string, unknown> => Boolean(row && typeof row === 'object')).map(mapPayment);
};

export const getRecentUpiPayees = (): Promise<RecentUpiPayee[]> =>
  MOCK_PAYMENTS ? mock.getRecentUpiPayees() : notAvailable();

export const listApprovalTasks = () =>
  MOCK_PAYMENTS ? mock.listApprovalTasks() : notAvailable();

export const claimApprovalTask = (transactionId: string, checkerId?: string) =>
  MOCK_PAYMENTS ? mock.claimApprovalTask(transactionId, checkerId) : notAvailable();

export const approveApprovalTask = (transactionId: string) =>
  MOCK_PAYMENTS ? mock.approveApprovalTask(transactionId) : notAvailable();

export const rejectApprovalTask = (transactionId: string, reason: string) =>
  MOCK_PAYMENTS ? mock.rejectApprovalTask(transactionId, reason) : notAvailable();
